import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import EventArtistSearch from "./EventArtistSearch";
import { haptic } from "../utils/haptic";

// Mobile Thumb Zone Search Bar - Fixed to bottom third of screen
const MobileSearchBar = ({ onSelect, isVisible = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Only render on mobile
  if (!isMobile || !isVisible) return null;

  const handleExpand = () => {
    haptic("light"); // 15ms haptic on thumb zone tap
    setIsExpanded(true);
  };

  const handleSelect = (selection) => {
    haptic("medium"); // Feedback on selection
    onSelect(selection);
    setIsExpanded(false);
  };

  return (
    <>
      {/* Backdrop when expanded */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[8999]"
          onClick={() => setIsExpanded(false)}
        />
      )}
      
      {/* Floating Search Bar - Thumb Zone (bottom third) */}
      <div 
        className={`fixed left-4 right-4 z-[9000] transition-all duration-300 ease-out ${
          isExpanded 
            ? "bottom-1/3 transform -translate-y-1/2" 
            : "bottom-24"
        }`}
      >
        {isExpanded ? (
          <div className="glass-strong rounded-2xl shadow-2xl border border-border/50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/30">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Find Events
              </span>
              <button 
                onClick={() => setIsExpanded(false)}
                className="w-8 h-8 rounded-full glass flex items-center justify-center"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4">
              <EventArtistSearch 
                onSelect={handleSelect}
                placeholder="Search events, artists..."
              />
            </div>
          </div>
        ) : (
          <button
            onClick={handleExpand}
            className="w-full glass-strong rounded-2xl p-4 shadow-2xl border border-border/50 flex items-center gap-3 active:scale-[0.98] transition-transform"
            data-testid="mobile-search-trigger"
          >
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Search className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 text-left">
              <div className="font-medium text-foreground text-sm">Find your event</div>
              <div className="text-xs text-muted-foreground">Search artists, venues...</div>
            </div>
            <div className="font-mono text-xs text-primary px-2 py-1 rounded-full bg-primary/10">
              128BPM
            </div>
          </button>
        )}
      </div>
    </>
  );
};

export default MobileSearchBar;
